import { Controller } from "@hotwired/stimulus"

// Expert's Insight playback on the lesson reader.
//
// Video still opens the shared player in a dimmed lightbox: opening starts
// playback, closing pauses it. Closes on a backdrop click, the close button,
// or Escape; locks body scroll while open and restores focus to the thumbnail.
//
// Audio stays on the card (TMS-1044). The existing Listen control toggles
// play/pause on a visually hidden media-player engine, updates its visible
// label and accessible name, and returns to Listen when the clip ends. No
// overlay, scroll lock, or duplicated expert image.
export default class extends Controller {
  static targets = ["overlay", "closeButton", "toggle", "playLabel", "playIcon"]
  static values = { title: String }

  connect() {
    this.boundSync = () => this.syncControl()
    this.boundEnded = () => this.handleEnded()
    this.mediaElement?.addEventListener("play", this.boundSync)
    this.mediaElement?.addEventListener("pause", this.boundSync)
    this.mediaElement?.addEventListener("ended", this.boundEnded)
  }

  disconnect() {
    this.mediaElement?.removeEventListener("play", this.boundSync)
    this.mediaElement?.removeEventListener("pause", this.boundSync)
    this.mediaElement?.removeEventListener("ended", this.boundEnded)
    this.mediaElement?.pause()
    if (this.hasOverlayTarget) document.body.style.overflow = ""
  }

  open(event) {
    this.lastFocused = event.currentTarget
    this.overlayTarget.hidden = false
    this.overlayTarget.setAttribute("aria-hidden", "false")
    // Add the visible class on the next frame so the opacity transition runs
    // from the just-unhidden state instead of being skipped.
    requestAnimationFrame(() => this.overlayTarget.classList.add("is-open"))
    document.body.style.overflow = "hidden"
    this.closeButtonTarget.focus()
    this.mediaElement?.play()?.catch(() => {})
  }

  close() {
    if (!this.lightboxOpen) return

    this.mediaElement?.pause()
    this.overlayTarget.classList.remove("is-open")
    this.overlayTarget.hidden = true
    this.overlayTarget.setAttribute("aria-hidden", "true")
    document.body.style.overflow = ""
    if (this.lastFocused) this.lastFocused.focus()
  }

  // Close only when the dimmed backdrop itself is clicked — clicking the player
  // (a child of the overlay) should keep the lightbox open.
  backdropClose(event) {
    if (event.target === this.overlayTarget) this.close()
  }

  closeOnEscape() {
    if (this.lightboxOpen) this.close()
  }

  toggle() {
    if (this.isPlaying) {
      this.mediaElement.pause()
      return
    }

    this.mediaElement?.play()?.catch(() => {})
  }

  handleEnded() {
    if (this.mediaElement) this.mediaElement.currentTime = 0
    this.syncControl()
  }

  syncControl() {
    const playing = this.isPlaying

    this.element.classList.toggle("is-playing", playing)

    if (this.hasPlayLabelTarget) {
      this.playLabelTarget.textContent = playing ? "Pause" : "Listen"
    }

    if (this.hasPlayIconTarget) {
      this.playIconTarget.classList.toggle("ph-play", !playing)
      this.playIconTarget.classList.toggle("ph-pause", playing)
    }

    if (this.hasToggleTarget) {
      const verb = playing ? "Pause" : "Listen to"
      this.toggleTarget.setAttribute("aria-label", `${verb} ${this.titleValue}`.trim())
    }
  }

  get isPlaying() {
    const media = this.mediaElement
    return !!media && !media.paused && !media.ended
  }

  get lightboxOpen() {
    return this.hasOverlayTarget && !this.overlayTarget.hidden
  }

  get mediaElement() {
    return this.element.querySelector("audio, video")
  }
}
